<script setup>

import SummaryNamedPrice from "./SummaryNamedPrice.vue";

defineEmits(["editClick"]);

const props = defineProps({
    ticketAndPricingData: {
        type: Object,
        required: true,
    },
    allEventsAmount: {
        type: Number,
        required: false,
        default: 0,
    },
    eventType: {
        type: String,
        required: true,
        default: "",
    },
});

const priceFixed = (price) => parseFloat(price).toFixed(2);

</script>

<template>
    <div class="d-flex flex-column mt-3r" data-test="summary-tickets-and-prices">
        <div class="d-flex flex-row justify-content-between">
            <div class="simple-event-title" data-test="summary-price-title">Tickets and price</div>
            <div @click="$emit('editClick', 'price')" class="card-text cursor-pointer"
                 data-test="summary-tickets-and-prices-edit">Edit
            </div>
        </div>

        <section v-if="ticketAndPricingData === undefined">
            <div class="mt-3 pl-3">
                No price described
            </div>
        </section>
        <section v-else>
            <div class="d-flex flex-row justify-content-between mt-3 pl-3" data-test="summary-selling-tickets">
                <div>Selling tickets on Loopla</div>
                <div class="event-data">{{ ticketAndPricingData?.sellingTicket ? "Yes" : "No" }}</div>
            </div>

            <div v-if="ticketAndPricingData?.sellingTicket" class="d-flex flex-row justify-content-between mt-3 pl-3"
                 data-test="summary-available-spaces">
                <div>Spaces available</div>
                <div class="event-data">{{ ticketAndPricingData?.spaceAvailable }}</div>
            </div>

            <div class="d-flex flex-row justify-content-between mt-3 pl-3" data-test="summary-free-to-attend">
                <div>Free to attend</div>
                <div class="event-data">{{ ticketAndPricingData?.isFree ? "Yes" : "No" }}</div>
            </div>

            <section v-if="ticketAndPricingData?.isFree === false">
                <section v-if="ticketAndPricingData.isRecurring">

                    <div
                        v-if="ticketAndPricingData.prices.taster.isActive"
                        class="d-flex flex-row justify-content-between mt-3 pl-3"
                        data-test="summary-allow-tester"
                    >
                        <div>Allow customers to attend a taster {{ props.eventType?.toLowerCase() }}</div>
                        <div class="event-data">{{ ticketAndPricingData.prices.taster.isActive ? "Yes" : "No" }}</div>
                    </div>

                    <div
                        v-if="ticketAndPricingData.prices.taster.isActive"
                        class="d-flex flex-row justify-content-between mt-3 pl-3"
                        data-test="summary-tester-price"
                    >
                        <div>Taster price {{ ticketAndPricingData.prices.taster.type !== "free" ?
                            "(including VAT)" : ""}}</div>
                        <div class="event-data">{{
                            ticketAndPricingData.prices.taster.type === "free"
                                ? "Free"
                                : `Paid:
                                £${priceFixed(ticketAndPricingData.prices.taster.paidPrice)}`
                        }}
                        </div>
                    </div>

                    <div v-if="ticketAndPricingData.prices.single.isActive"
                         class="d-flex flex-row justify-content-between mt-3 pl-3"
                         data-test="summary-price-per-event-with-repetition">
                        <div>Price per {{ props.eventType?.toLowerCase() }} (including VAT)</div>
                        <SummaryNamedPrice
                            :base-price="ticketAndPricingData.prices.single.basePrice"
                            :named-price-list="ticketAndPricingData.prices.single.namedPriceList"
                            :has-named-price-list="ticketAndPricingData.prices.single.hasNamedPriceList"
                        />
                    </div>

                    <div v-if="ticketAndPricingData.prices.daily.isActive"
                         class="d-flex flex-row justify-content-between mt-3 pl-3"
                         data-test="summary-price-per-event-with-repetition">
                        <div>Price for all {{ allEventsAmount }} {{ props.eventType?.toLowerCase() }} (including VAT)</div>
                        <SummaryNamedPrice
                            :base-price="ticketAndPricingData.prices.daily.basePrice"
                            :named-price-list="ticketAndPricingData.prices.daily.namedPriceList"
                            :has-named-price-list="ticketAndPricingData.prices.daily.hasNamedPriceList"
                        />
                    </div>

                    <div v-if="ticketAndPricingData.prices.weekly.isActive"
                         class="d-flex flex-row justify-content-between mt-3 pl-3"
                         data-test="summary-price-for-all-with-repetition">
                        <div>Price for all {{ allEventsAmount }} {{ props.eventType?.toLowerCase() }} (including VAT)</div>
                        <SummaryNamedPrice
                            :base-price="ticketAndPricingData.prices.weekly.basePrice"
                            :named-price-list="ticketAndPricingData.prices.weekly.namedPriceList"
                            :has-named-price-list="ticketAndPricingData.prices.weekly.hasNamedPriceList"
                        />
                    </div>

                    <div v-if="ticketAndPricingData.prices.weekly.isActive"
                         class="d-flex flex-row justify-content-between mt-3 pl-3"
                         data-test="summary-price-for-all-with-repetition">
                        <div>Pro-rata</div>
                        <div class="event-data">{{ ticketAndPricingData.prices.weekly.proRata ? "Yes" : "No" }}</div>
                    </div>

                </section>
                <section
                    v-if="!ticketAndPricingData.isRecurring &&
                        ticketAndPricingData.prices?.single?.isActive">
                    <div class="d-flex flex-row justify-content-between mt-3 pl-3"
                         data-test="summary-price-per-event-type">
                        <div>Price per {{ props.eventType?.toLowerCase() }} (including VAT)</div>
                        <SummaryNamedPrice
                            :base-price="ticketAndPricingData.prices.single.basePrice"
                            :named-price-list="ticketAndPricingData.prices.single.namedPriceList"
                            :has-named-price-list="ticketAndPricingData.prices.single.hasNamedPriceList"
                        />
                    </div>
                </section>
            </section>
        </section>
    </div>
</template>

<style scoped>
.event-data {
    font-weight: 400;
    color: #636066;
}
</style>
